import { PropsWithChildren, useMemo } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { Maybe } from '@paradigm/utils/types';
import { findBaseProductCode } from '#/products/domain';
import { Rfq } from '#/unified-rfqs/entities/rfq';
import { isDrfq } from '#/unified-rfqs/entities/drfq/domain';
import {
  primaryProductCode,
  strategyProductCodes,
} from '#/unified-rfqs/utils/legs';
import { mostPrecisePricePrecision } from '#/unified-rfqs/utils/rfq-price-functions';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { Bbo } from '#/unified-rfqs/entities/drfq/bbo';
import {
  getBboLegs,
  getScreenQty,
  shouldShowIv,
  getBboOffset,
  getScreenPrice,
  parseIv,
} from '#/unified-rfqs/utils/bbo-functions';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { usePriceFormatterFn } from './hooks/useFormattedPrice';
import { useRfqContext } from './rfq-context';
import { WithFormattingProps, createFormattingProps } from './types';
import { formatProductQty } from './decimal-format';
import { useUnifiedBbo } from './rfqs/hooks';
import { isDatedRfqLegs } from './rfqs/legs-functions';

interface BboView {
  isLoading: boolean;
  data: GrfqBbo | Bbo | null;
  error: string | null;
  isDated: boolean;
  markPrice: WithFormattingProps<string | undefined>;
  bestBidPrice: WithFormattingProps<string | undefined>;
  bestAskPrice: WithFormattingProps<string | undefined>;
  bestBidQty: WithFormattingProps<string | undefined>;
  bestAskQty: WithFormattingProps<string | undefined>;
  bestBidIv: WithFormattingProps<Maybe<string>>;
  bestAskIv: WithFormattingProps<Maybe<string>>;
  markPriceIv: WithFormattingProps<Maybe<string>>;
  getBboOffset: (args: {
    bidPrice: Maybe<string>;
    askPrice: Maybe<string>;
  }) => ReturnType<typeof getBboOffset>;
}

const [useBboView, BboViewCtxProvider] = createSafeContext<BboView>('BboView');

export { useBboView };
/**
 *
 * Performs all transformation needed to display bbo data with correct formatting and displayed value
 * according to rfq and bbo values.
 *
 * WARNING: This provider depends on the RfqContext provider, so they should always be used together\
 * WARNING: Should be used in places that the RFQ is guaranteed to exist. That is: we should alway check
 * if RFQ is null before rendering this provider.
 * @param param0
 * @returns {BboView}
 */
export default function BboViewProvider({ children }: PropsWithChildren) {
  const { rfq } = useRfqContext();
  const baseProductCode = getBaseProductCode(rfq);

  const formatPrice = usePriceFormatterFn({
    rfqType: rfq?.type,
    productCode: baseProductCode,
    description: rfq?.description,
    side: null,
    // Same override the order and trade prices of this package use, so
    // the BBO does not round to a different number of places than the
    // prices it is compared against.
    precisionOverride: mostPrecisePricePrecision(
      rfq != null && isDrfq(rfq) ? strategyProductCodes(rfq.legs) : [],
      true,
    ),
  });

  const bbo = useUnifiedBbo();

  const view: BboView = useMemo(() => {
    const { price: bestAskPrice, quantity: bestAskQty } =
      bbo.data?.best_ask ?? {};
    const { price: bestBidPrice, quantity: bestBidQty } =
      bbo.data?.best_bid ?? {};

    const bboLegs = getBboLegs(bbo.data);
    const bidIv = bboLegs?.[0]?.best_bid_iv;
    const askIv = bboLegs?.[0]?.best_ask_iv;
    const markPriceIv = bboLegs?.[0]?.mark_price_iv;

    const isDated =
      rfq == null ? false : isDatedRfqLegs(rfq.legs, baseProductCode);

    const formatQty = (quantity: Maybe<string>) => {
      const screenQty = getScreenQty(quantity);
      return formatProductQty(
        screenQty == null ? NEGATIVE_SIGN : screenQty,
        baseProductCode ?? undefined,
        rfq?.type === 'drfq',
        isDated,
      );
    };

    const formatIv = (ivValue: Maybe<string>) => {
      if (!shouldShowIv(rfq)) return '';
      const ivParsed = parseIv(ivValue);
      return ivParsed != null ? `${ivParsed}%` : '';
    };

    return {
      data: bbo.data,
      error: bbo.error,
      isLoading: bbo.isLoading,
      isDated,
      markPrice: createFormattingProps(bbo.data?.mark_price, formatPrice),
      bestBidPrice: createFormattingProps(bestBidPrice, (price) =>
        formatPrice(getScreenPrice(bestBidQty, price), 'BUY'),
      ),
      bestAskPrice: createFormattingProps(bestAskPrice, (price) =>
        formatPrice(getScreenPrice(bestAskQty, price), 'SELL'),
      ),
      bestBidQty: createFormattingProps(bestBidQty, formatQty),
      bestAskQty: createFormattingProps(bestAskQty, formatQty),
      bestBidIv: createFormattingProps(bidIv, formatIv),
      bestAskIv: createFormattingProps(askIv, formatIv),
      markPriceIv: createFormattingProps(markPriceIv, formatIv),
      getBboOffset: ({ bidPrice, askPrice }) =>
        getBboOffset({
          askPrice,
          bidPrice,
          bestAskPrice,
          bestAskQty,
          bestBidPrice,
          bestBidQty,
          formatAsk: (price) => formatPrice(price, 'SELL'),
          formatBid: (price) => formatPrice(price, 'BUY'),
          productCode: baseProductCode,
        }),
    };
  }, [baseProductCode, bbo.data, bbo.error, bbo.isLoading, formatPrice, rfq]);

  return <BboViewCtxProvider value={view}>{children}</BboViewCtxProvider>;
}

const getBaseProductCode = (rfq: Maybe<Rfq>) => {
  if (rfq == null) return null;
  // findBaseProductCode picks a product by instrument kind across every
  // code, hedge included; once a package spans several products the
  // identity is the primary (ratio-1) strategy leg's product instead.
  // GRFQ is never multi-product, so it keeps the kind-based pick.
  if (!isDrfq(rfq)) return findBaseProductCode(rfq.product_codes);
  return primaryProductCode(rfq.legs) ?? findBaseProductCode(rfq.product_codes);
};
